import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Star, TrendingUp, TrendingDown, ArrowUpDown, Search, Clock } from 'lucide-react';
import { getTheme } from '../App';

interface KuCoinContract {
  symbol:                  string;
  baseCurrency:            string;
  markPrice:               number;
  indexPrice:              number;
  bestBidPrice:            number;   // futures bid
  bestAskPrice:            number;   // futures ask
  spotBidPrice:            number;   // spot bid
  spotAskPrice:            number;   // spot ask
  fundingFeeRate:          number;   // son funding (decimal)
  predictedFundingFeeRate: number;   // tahmini funding
  nextFundingRateTime:     number;   // ms remaining until next funding
  openInterest:            number;
  turnoverOf24h:           number;
  priceChgPct:             number;
}

interface Props { dark?: boolean; }

const FAVS_KEY = 'kucoin_favorites';

const KUCOIN_WS = window.location.port === '3000' || window.location.port === '3001'
  ? `ws://${window.location.hostname}:8882`
  : 'wss://ws.barbarosquant.org/kucoin-ws';

type SortKey = 'volume' | 'alpha' | 'change' | 'last_funding' | 'est_funding' | 'oi';
type SortDir = 'asc' | 'desc';

function loadFavs(): string[] {
  try { return JSON.parse(localStorage.getItem(FAVS_KEY) || '[]'); }
  catch { return []; }
}

function formatCountdown(ms: number): string {
  if (ms <= 0) return '—';
  const totalSec = Math.floor(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (h > 0) return `${h}s ${m}d`;
  return `${m}d ${s.toString().padStart(2, '0')}s`;
}

function fmtPrice(p: number): string {
  if (p >= 1000) return p.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  if (p >= 1)    return p.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 4 });
  return p.toFixed(6);
}

export default function KuCoinFunding({ dark = true }: Props) {
  const t = getTheme(dark);

  const [contracts,  setContracts]  = useState<KuCoinContract[]>([]);
  const [lastUpdate, setLastUpdate] = useState('—');
  const [fetchedAt,  setFetchedAt]  = useState(0);
  const [now,        setNow]        = useState(Date.now());
  const [connected,  setConnected]  = useState(false);

  const [favorites,    setFavs]         = useState<string[]>(loadFavs);
  const [search,       setSearch]       = useState('');
  const [sortKey,      setSortKey]      = useState<SortKey>('volume');
  const [sortDir,      setSortDir]      = useState<SortDir>('desc');
  const [showFavsOnly, setShowFavsOnly] = useState(false);

  const wsRef    = useRef<WebSocket | null>(null);
  const nowTimer = useRef<ReturnType<typeof setInterval> | null>(null);

  // Kendi WebSocket bağlantısı (kucoin_server.py — port 8882)
  useEffect(() => {
    let dead = false;
    let retry: ReturnType<typeof setTimeout>;
    function connect() {
      if (dead) return;
      const ws = new WebSocket(KUCOIN_WS);
      wsRef.current = ws;
      ws.onopen = () => {
        setConnected(true);
        ws.send(JSON.stringify({ type: 'kucoin_get_state' }));
      };
      ws.onmessage = (ev) => {
        try {
          const msg = JSON.parse(ev.data);
          if (msg.type === 'kucoin_tickers' && Array.isArray(msg.payload)) {
            const ft = Date.now();
            setContracts(msg.payload as KuCoinContract[]);
            setFetchedAt(ft);
            setLastUpdate(new Date(ft).toLocaleTimeString('tr-TR'));
          }
        } catch {}
      };
      ws.onclose = () => { setConnected(false); if (!dead) retry = setTimeout(connect, 3000); };
      ws.onerror = () => ws.close();
    }
    connect();
    return () => { dead = true; clearTimeout(retry); wsRef.current?.close(); };
  }, []);

  // Canlı geri sayım için saniyede bir now güncelle
  useEffect(() => {
    nowTimer.current = setInterval(() => setNow(Date.now()), 1000);
    return () => { if (nowTimer.current) clearInterval(nowTimer.current); };
  }, []);

  const toggleFav = (sym: string) => {
    setFavs(prev => {
      const next = prev.includes(sym) ? prev.filter(s => s !== sym) : [...prev, sym];
      localStorage.setItem(FAVS_KEY, JSON.stringify(next));
      return next;
    });
  };

  const cycleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    else { setSortKey(key); setSortDir('desc'); }
  };

  // XBT → BTC dönüşümü
  const displayName = (c: KuCoinContract) =>
    c.baseCurrency === 'XBT' ? 'BTC' : c.baseCurrency;

  const filtered = contracts
    .filter(c => {
      if (showFavsOnly && !favorites.includes(c.symbol)) return false;
      const name = displayName(c).toLowerCase();
      const q    = search.toLowerCase();
      return !q || name.includes(q) || c.symbol.toLowerCase().includes(q);
    })
    .sort((a, b) => {
      if (sortKey === 'alpha') {
        return sortDir === 'asc'
          ? displayName(a).localeCompare(displayName(b))
          : displayName(b).localeCompare(displayName(a));
      }
      let va = 0, vb = 0;
      if (sortKey === 'volume')       { va = a.turnoverOf24h;           vb = b.turnoverOf24h; }
      if (sortKey === 'change')       { va = a.priceChgPct;             vb = b.priceChgPct; }
      if (sortKey === 'last_funding') { va = a.fundingFeeRate;          vb = b.fundingFeeRate; }
      if (sortKey === 'est_funding')  { va = a.predictedFundingFeeRate; vb = b.predictedFundingFeeRate; }
      if (sortKey === 'oi')           { va = a.openInterest;            vb = b.openInterest; }
      return sortDir === 'desc' ? vb - va : va - vb;
    });

  // Favoriler her zaman üstte
  const sorted = [
    ...filtered.filter(c => favorites.includes(c.symbol)),
    ...filtered.filter(c => !favorites.includes(c.symbol)),
  ];

  // Özet istatistikler
  const avgLast      = contracts.length > 0
    ? contracts.reduce((s, c) => s + c.fundingFeeRate,          0) / contracts.length : 0;
  const avgEst       = contracts.length > 0
    ? contracts.reduce((s, c) => s + c.predictedFundingFeeRate, 0) / contracts.length : 0;
  const maxAbsEst    = contracts.reduce((mx, c) => Math.max(mx, Math.abs(c.predictedFundingFeeRate)), 0);
  const positiveEst  = contracts.filter(c => c.predictedFundingFeeRate > 0).length;
  const negativeEst  = contracts.filter(c => c.predictedFundingFeeRate < 0).length;

  const SortBtn = ({ k, label }: { k: SortKey; label: string }) => (
    <button onClick={() => cycleSort(k)}
      className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-medium transition-colors ${
        sortKey === k
          ? 'bg-[#4A9FE8] text-[#061530] font-bold'
          : `${t.btnSecondary} opacity-70`
      }`}>
      {label}
      {sortKey === k && <ArrowUpDown size={10} />}
    </button>
  );

  const card = `${t.card} border ${t.cardBorder} rounded-xl`;

  return (
    <div className="p-6 space-y-4">

      {/* ── Başlık ── */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className={`text-xl font-bold ${t.text}`}>KuCoin Vadeli — Funding</h2>
          <p className={`text-xs mt-0.5 ${t.muted}`}>
            Canlı son & tahmini funding oranları · {contracts.length} kontrat · 8s periyot
          </p>
        </div>
        <div className={`flex items-center gap-2 text-xs ${t.muted}`}>
          <div className={`w-2 h-2 rounded-full ${!connected ? 'bg-rose-400' : contracts.length === 0 ? 'bg-yellow-400 animate-pulse' : 'bg-green-400'}`} />
          {!connected ? 'Backend bekleniyor...' : contracts.length === 0 ? 'KuCoin verisi bekleniyor...' : `Son: ${lastUpdate}`}
        </div>
      </div>

      {/* ── İstatistik kartları ── */}
      <div className="grid grid-cols-5 gap-3">
        {[
          {
            label: 'Ort. Son Funding',
            value: `${avgLast >= 0 ? '+' : ''}${(avgLast * 100).toFixed(5)}%`,
            pos:   avgLast >= 0,
          },
          {
            label: 'Ort. Tah. Funding',
            value: `${avgEst >= 0 ? '+' : ''}${(avgEst * 100).toFixed(5)}%`,
            pos:   avgEst >= 0,
          },
          {
            label: 'En Yüksek |Est.|',
            value: `${(maxAbsEst * 100).toFixed(5)}%`,
            pos:   null,
          },
          {
            label: 'Est. Pozitif / Negatif',
            value: `${positiveEst} / ${negativeEst}`,
            pos:   null,
          },
          {
            label: 'Aktif Kontrat',
            value: String(contracts.length),
            pos:   null,
          },
        ].map(c => (
          <div key={c.label} className={`${card} p-4`}>
            <p className={`text-xs mb-1 ${t.muted}`}>{c.label}</p>
            <p className={`text-base font-bold font-mono ${
              c.pos === null ? t.text : c.pos ? t.positive : t.negative
            }`}>{c.value}</p>
          </div>
        ))}
      </div>

      {/* ── Kontroller ── */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border ${
          dark ? 'bg-[#0a2560]/60 border-[#003087]' : 'bg-white border-blue-300'
        }`}>
          <Search size={13} className={t.muted} />
          <input
            placeholder="Sembol ara..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className={`bg-transparent text-xs outline-none w-28 ${t.text}`}
          />
        </div>

        <button onClick={() => setShowFavsOnly(v => !v)}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
            showFavsOnly ? 'bg-[#4A9FE8] text-[#061530] font-bold' : t.btnSecondary
          }`}>
          <Star size={12} fill={showFavsOnly ? 'currentColor' : 'none'} />
          Favoriler {favorites.length > 0 && `(${favorites.length})`}
        </button>

        <div className={`h-4 w-px ${dark ? 'bg-[#0a2560]' : 'bg-blue-200'}`} />

        <span className={`text-xs ${t.muted}`}>Sırala:</span>
        <SortBtn k="volume"       label="Hacim" />
        <SortBtn k="alpha"        label="A-Z" />
        <SortBtn k="change"       label="24s %" />
        <SortBtn k="last_funding" label="|Son|" />
        <SortBtn k="est_funding"  label="|Est.|" />
        <SortBtn k="oi"           label="OI" />
      </div>

      {/* ── Tablo ── */}
      <div className={`${card} overflow-hidden`}>
        {contracts.length === 0 ? (
          <div className={`p-10 text-center ${t.muted} text-sm`}>
            {connected ? 'KuCoin verisi bekleniyor... (backend her 5s yayınlar)' : 'Backend bağlantısı bekleniyor...'}
          </div>
        ) : sorted.length === 0 ? (
          <div className={`p-10 text-center ${t.muted} text-sm`}>
            {showFavsOnly ? 'Favori kontrat eklenmedi.' : 'Eşleşen kontrat bulunamadı.'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className={`${t.tableHeader} border-b`}>
                  <th className="px-3 py-2.5 w-8"></th>
                  <th className="px-3 py-2.5 text-left">Sembol</th>
                  <th className={`px-3 py-2.5 text-right ${dark ? 'text-emerald-400' : 'text-emerald-600'}`}>Spot Bid</th>
                  <th className={`px-3 py-2.5 text-right ${dark ? 'text-rose-400' : 'text-rose-600'}`}>Spot Ask</th>
                  <th className={`px-3 py-2.5 text-right ${dark ? 'text-emerald-300' : 'text-emerald-700'}`}>Fut Bid</th>
                  <th className={`px-3 py-2.5 text-right ${dark ? 'text-rose-300' : 'text-rose-700'}`}>Fut Ask</th>
                  <th className="px-3 py-2.5 text-right">Mark</th>
                  <th className="px-3 py-2.5 text-right">24s %</th>
                  <th className={`px-3 py-2.5 text-right font-bold ${dark ? 'text-cyan-300' : 'text-cyan-700'}`}>
                    Son Funding
                  </th>
                  <th className={`px-3 py-2.5 text-right font-bold ${dark ? 'text-yellow-300' : 'text-yellow-700'}`}>
                    Tah. Funding
                  </th>
                  <th className="px-3 py-2.5 text-right">Sonraki</th>
                  <th className="px-3 py-2.5 text-right">Spread %</th>
                  <th className="px-3 py-2.5 text-right">OI ($M)</th>
                  <th className="px-3 py-2.5 text-right">24s Hacim</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map(c => {
                  const isFav  = favorites.includes(c.symbol);
                  const name   = displayName(c);
                  const lastF  = c.fundingFeeRate * 100;
                  const estF   = c.predictedFundingFeeRate * 100;
                  const spread = (c.markPrice > 0 && c.bestBidPrice > 0 && c.bestAskPrice > 0)
                    ? ((c.bestAskPrice - c.bestBidPrice) / c.markPrice) * 100
                    : 0;

                  // nextFundingRateTime ms remaining; canlı geri sayım için elapsed çıkar
                  const elapsed = fetchedAt > 0 ? now - fetchedAt : 0;
                  const msLeft  = Math.max(0, c.nextFundingRateTime - elapsed);

                  // Yüksek mutlak funding vurgulama eşiği: %0.05
                  const highFunding = Math.abs(estF) >= 0.05;

                  return (
                    <tr key={c.symbol}
                      className={`border-b ${t.tableRow} ${isFav ? (dark ? 'bg-[#4A9FE8]/5' : 'bg-yellow-50/60') : ''}`}>

                      {/* Favori yıldız */}
                      <td className="px-3 py-2 text-center">
                        <button onClick={() => toggleFav(c.symbol)}
                          className={`transition-colors ${isFav ? 'text-[#4A9FE8]' : `${t.muted} hover:text-[#4A9FE8]`}`}>
                          <Star size={13} fill={isFav ? 'currentColor' : 'none'} />
                        </button>
                      </td>

                      {/* Sembol */}
                      <td className="px-3 py-2">
                        <span className={`font-bold ${t.gold}`}>{name}</span>
                        <span className={`ml-1.5 text-[10px] ${t.muted} opacity-60`}>{c.symbol}</span>
                        {isFav && <span className="ml-1 text-[10px] text-[#4A9FE8] opacity-60">★</span>}
                      </td>

                      {/* Spot Bid */}
                      <td className={`px-3 py-2 text-right font-mono ${t.positive}`}>
                        {c.spotBidPrice > 0 ? `$${fmtPrice(c.spotBidPrice)}` : '—'}
                      </td>

                      {/* Spot Ask */}
                      <td className={`px-3 py-2 text-right font-mono ${t.negative}`}>
                        {c.spotAskPrice > 0 ? `$${fmtPrice(c.spotAskPrice)}` : '—'}
                      </td>

                      {/* Fut Bid */}
                      <td className={`px-3 py-2 text-right font-mono ${dark ? 'text-emerald-300' : 'text-emerald-700'}`}>
                        {c.bestBidPrice > 0 ? `$${fmtPrice(c.bestBidPrice)}` : '—'}
                      </td>

                      {/* Fut Ask */}
                      <td className={`px-3 py-2 text-right font-mono ${dark ? 'text-rose-300' : 'text-rose-700'}`}>
                        {c.bestAskPrice > 0 ? `$${fmtPrice(c.bestAskPrice)}` : '—'}
                      </td>

                      {/* Mark price */}
                      <td className={`px-3 py-2 text-right font-mono ${t.text}`}>
                        ${fmtPrice(c.markPrice)}
                      </td>

                      {/* 24s değişim */}
                      <td className={`px-3 py-2 text-right font-mono font-bold ${c.priceChgPct >= 0 ? t.positive : t.negative}`}>
                        <span className="flex items-center justify-end gap-0.5">
                          {c.priceChgPct >= 0
                            ? <TrendingUp  size={11} />
                            : <TrendingDown size={11} />}
                          {c.priceChgPct >= 0 ? '+' : ''}{(c.priceChgPct * 100).toFixed(2)}%
                        </span>
                      </td>

                      {/* Son Funding */}
                      <td className={`px-3 py-2 text-right font-mono font-semibold ${lastF >= 0 ? t.positive : t.negative}`}>
                        {lastF >= 0 ? '+' : ''}{lastF.toFixed(4)}%
                      </td>

                      {/* Tahmini Funding */}
                      <td className={`px-3 py-2 text-right font-mono font-bold ${
                        highFunding
                          ? estF >= 0
                            ? 'text-emerald-300 bg-emerald-900/20'
                            : 'text-rose-300 bg-rose-900/20'
                          : estF >= 0 ? t.positive : t.negative
                      }`}>
                        {estF >= 0 ? '+' : ''}{estF.toFixed(4)}%
                        {highFunding && <span className="ml-1 text-[9px] opacity-70">▲</span>}
                      </td>

                      {/* Sonraki funding */}
                      <td className={`px-3 py-2 text-right font-mono ${t.muted}`}>
                        <span className="flex items-center justify-end gap-1">
                          <Clock size={10} />
                          {formatCountdown(msLeft)}
                        </span>
                      </td>

                      {/* Spread */}
                      <td className={`px-3 py-2 text-right font-mono ${spread > 0.1 ? t.negative : t.muted}`}>
                        {spread.toFixed(4)}%
                      </td>

                      {/* OI */}
                      <td className={`px-3 py-2 text-right ${t.muted}`}>
                        ${(c.openInterest * c.markPrice / 1e6).toFixed(2)}M
                      </td>

                      {/* 24s Hacim */}
                      <td className={`px-3 py-2 text-right font-semibold ${t.text}`}>
                        ${(c.turnoverOf24h / 1e6).toFixed(2)}M
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <p className={`text-[10px] ${t.muted} text-right`}>
        Kaynak: KuCoin Futures API · Backend her 5s yeniler
      </p>
    </div>
  );
}
